import React, { useCallback, useState, useMemo } from "react";
import { debounce } from "lodash";
import useApiEp from "../../hooks/useApiEp";
import TitleActionsContainer from "../../widgets/TitleActionsContainer";
import PageTitle from "../../widgets/PageTitle";
import {
  Button,
  Container,
  Stack,
  TextField,
  Chip,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DataTable from "../../widgets/DataTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import CategoryChip from "./CategoryChip";
import QueueStatusChip from "./QueueStatusChip";

const RdCampaignsList = () => {
  const [searchParams] = useSearchParams();
  const rcsType = searchParams.get("type");
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sq, setSq] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // This will be the debounced value
  const { data, isLoading, isError, error } = useApiEp(
    `/campaign/get-campaigns?page=${page}&name=${searchQuery}&type=${
      rcsType ? rcsType : ""
    }`
  );

  // Create debounced function
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchQuery(value);
        setPage(1); // Reset to first page when searching
      }, 500),
    []
  );

  const handleSearchChange = (e) => {
    setSq(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleAction = useCallback((action, id) => {
    switch (action) {
      case "view":
        navigate(`/home/${id}/campaign-detail?type=${rcsType}`);
        break;
      default:
        console.warn("unknown action", id);
    }
  }, []);

  // Cleanup debounce on unmount
  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <Container sx={{ mt: 3 }}>
      <TitleActionsContainer>
        <PageTitle>Campaigns List</PageTitle>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            width: { xs: "100%", sm: "auto" },
            mt: { xs: 1, sm: 0 },
          }}
        >
          <TextField
            size="small"
            value={sq}
            onChange={handleSearchChange}
            placeholder="Search Campaign"
            InputProps={{ startAdornment: <SearchIcon fontSize="small" /> }}
          />
        </Stack>
      </TitleActionsContainer>
      <Alert severity="info">Click view to monitor live progress</Alert>
      <DataTable
        sx={{ mb: 1 }}
        columns={[
          {
            id: "serial",
            label: "S.No.",
            render: (_v, _row, idx) => (page - 1) * limit + idx + 1,
          },
          { id: "campaignName", label: "Campaign" },
          { id: "totalContacts", label: "Total Contacts" },
          {
            id: "completionPercentage",
            label: "Completion %",
            render: (value) => (
              <span
                style={{
                  color:
                    value >= 80
                      ? "#4CAF50" // Green for high completion
                      : value >= 50
                      ? "#FFC107" // Yellow for medium
                      : "#F44336", // Red for low
                  fontWeight: "bold",
                }}
              >
                {value}%
              </span>
            ),
          },
          {
            id: "status",
            label: "Status",
            render: (v) => <QueueStatusChip status={v} />,
          },
          {
            id: "category",
            label: "Category",
            render: (_v, row) => (
              <Box display="flex" flexDirection="column">
                {<CategoryChip category={row.category} />}
                {row.category?.toLowerCase() === "scheduled" &&
                  row.scheduledDate && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 0.5, color: "text.secondary" }}
                    >
                      {new Date(row.scheduledDate).toLocaleString("en-IN", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </Typography>
                  )}
              </Box>
            ),
          },
          {
            id: "createdOn",
            label: "Created On",
            render: (v) =>
              new Date(v).toLocaleString("en-IN", {
                dateStyle: "medium",
                timeStyle: "short",
              }),
          },
        ]}
        actions={["view"]}
        rows={data?.data ?? []}
        page={page}
        limit={limit}
        totalPages={data?.totalPages ?? 1}
        onPageChange={setPage}
        onAction={handleAction}
      />
    </Container>
  );
};

export default RdCampaignsList;
